// [COPIAR CÓDIGO]
//
/* GS://PROTOCOLO
 * Arquivo: src/pages/ProductsPage.jsx
 * Dossiê de Projeto e Manual de Colaboração: Ecossistema GoldenSisters
 * Versão: 5.3 – RELEASE: Produção (Clean Build)
 * Status: ESTÁVEL
 *
 * Mudanças desta versão:
 * - CLEANUP: Remoção de imports de ícones não utilizados na barra de resumo simplificada.
 * - MANTAIN: Correção de dados (cost) e Layout (Sticky Header) preservados.
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { db } from '../firebase/config';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  query,
  orderBy,
} from 'firebase/firestore';
import {
  Plus,
  Printer,
  Edit2,
  Trash2,
  Search,
  ArrowUpDown,
  Image as ImageIcon,
  Star
} from 'lucide-react';
import ProductForm from '../components/forms/ProductForm';

const LOCATION_OPTIONS = [
  'Todas',
  'Em Loja',
  'Em Estoque',
  'Em Trânsito',
  'Vitrine',
  'Manutenção Externa',
  'Outro',
];

const STATUS_OPTIONS = [
  'Todos',
  'Disponível',
  'Alugado',
  'Vendido',
  'Defeito',
  'Perdido',
  'Aguardando Precificação',
  'Aguardando Manutenção',
  'Em Banho',
  'Outro',
];

export default function ProductsPage({ openModal, closeModal }) {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [rows, setRows] = useState([]);

  // Filtros
  const [stockFilter, setStockFilter] = useState('Todos');
  const [locationFilter, setLocationFilter] = useState('Todas');
  const [statusFilter, setStatusFilter] = useState('Todos');
  const [pdvFilter, setPdvFilter] = useState('Todos');
  const [imageFilter, setImageFilter] = useState('Todos');
  const [recommendedFilter, setRecommendedFilter] = useState(false);
  const [qSearch, setQSearch] = useState('');

  // Estado de Ordenação
  const [sortOption, setSortOption] = useState('name_asc');

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const snap = await getDocs(
        query(collection(db, 'products'), orderBy('name', 'asc'))
      );
      const list = snap.docs.map((d) => ({
        ...d.data(),
        id: d.id,
      }));
      setRows(list);
    } catch (e) {
      setErr(e?.message || 'Erro ao carregar produtos.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const fmtBRL = (n) =>
    typeof n === 'number'
      ? n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
      : '—';

  const toNumberOrNull = useCallback((v) => {
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    if (typeof v === 'string' && v.trim() !== '') {
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  }, []);

  const getEffectiveSalePrice = useCallback((p) => {
    const decided = toNumberOrNull(p?.decidedPrice);
    if (decided != null && decided > 0) return decided;

    const baseVal = toNumberOrNull(p?.price);
    if (baseVal != null && baseVal > 0) return baseVal;

    return 0;
  }, [toNumberOrNull]);

  const getDisplayName = (p) => {
    if (p.name && p.name.trim()) return p.name;
    return p.supplierProductName || p.description || '';
  };

  const currentRecommendedCount = useMemo(
    () => rows.filter((p) => p?.displaySettings?.onSeparamos === true).length,
    [rows]
  );

  const processedRows = useMemo(() => {
    const text = (qSearch || '').trim().toLowerCase();

    let result = rows.filter((p) => {
      const commercialName = (p.name || '').toLowerCase();
      const technicalName = (p.supplierProductName || '').toLowerCase();
      const sku = (p.sku || '').toLowerCase();

      const matchesText =
        !text ||
        commercialName.includes(text) ||
        technicalName.includes(text) ||
        sku.includes(text);

      const stock = Number(p.stock ?? 0);
      const matchesStock =
        stockFilter === 'Todos' ||
        (stockFilter === 'Com' && stock > 0) ||
        (stockFilter === 'Sem' && stock === 0);

      const loc = p.location || 'Em Loja';
      const matchesLocation =
        locationFilter === 'Todas' || loc === locationFilter;

      const st = p.status || 'Disponível';
      const matchesStatus = statusFilter === 'Todos' || st === statusFilter;

      const inPOS = !!p.pdvEnabled;
      const matchesPDV =
        pdvFilter === 'Todos' ||
        (pdvFilter === 'PDV' && inPOS) ||
        (pdvFilter === 'FORA_PDV' && !inPOS);

      const hasImage = Array.isArray(p.images) && p.images.length > 0;
      const matchesImage =
        imageFilter === 'Todos' ||
        (imageFilter === 'Com' && hasImage) ||
        (imageFilter === 'Sem' && !hasImage);

      const isRecommended = p?.displaySettings?.onSeparamos === true;
      const matchesRecommended = !recommendedFilter || isRecommended;

      return (
        matchesText &&
        matchesStock &&
        matchesLocation &&
        matchesStatus &&
        matchesPDV &&
        matchesImage &&
        matchesRecommended
      );
    });

    return result.sort((a, b) => {
      switch (sortOption) {
        case 'name_asc':
          return getDisplayName(a)
            .toLowerCase()
            .localeCompare(getDisplayName(b).toLowerCase());
        case 'name_desc':
          return getDisplayName(b)
            .toLowerCase()
            .localeCompare(getDisplayName(a).toLowerCase());
        case 'price_asc':
          return getEffectiveSalePrice(a) - getEffectiveSalePrice(b);
        case 'price_desc':
          return getEffectiveSalePrice(b) - getEffectiveSalePrice(a);
        case 'newest': {
          const dateA = a.createdAt?.seconds || 0;
          const dateB = b.createdAt?.seconds || 0;
          return dateB - dateA;
        }
        default:
          return 0;
      }
    });
  }, [
    rows,
    qSearch,
    stockFilter,
    locationFilter,
    statusFilter,
    pdvFilter,
    imageFilter,
    recommendedFilter,
    sortOption,
    getEffectiveSalePrice
  ]);

  // Cálculo dos Totais Financeiros (Resumo)
  // Corrigido para ler o campo 'cost'
  const summary = useMemo(() => {
    return processedRows.reduce((acc, row) => {
        const qtd = Number(row.stock || 0);
        const sale = getEffectiveSalePrice(row);
        const cost = Number(row.cost || 0);

        acc.count += 1; 
        acc.totalStock += qtd; 
        acc.saleValue += (sale * qtd); 
        acc.costValue += (cost * qtd); 
        return acc;
    }, { count: 0, totalStock: 0, saleValue: 0, costValue: 0 });
  }, [processedRows, getEffectiveSalePrice]);

  const onPrint = () => {
    window.print();
  };

  const handleOpenForm = (productData = null) => {
    if (!openModal) return;
    openModal(
      <ProductForm
        productData={productData}
        onClose={closeModal}
        onSave={handleSaveProduct}
        maxSeparamos={5}
        currentRecommendedCount={currentRecommendedCount}
      />,
      productData ? `Editar Produto` : `Adicionar Novo Produto`,
      '4xl'
    );
  };

  const handleSaveProduct = async (dataToSave, id) => {
    const payload = {
      ...dataToSave,
      updatedAt: serverTimestamp(),
    };

    let docRef;

    if (id) {
      docRef = doc(db, 'products', id);
      await updateDoc(docRef, payload);
      return { id, ...dataToSave };
    } else {
      docRef = await addDoc(collection(db, 'products'), {
        ...payload,
        createdAt: serverTimestamp(),
      });
      return { id: docRef.id, ...dataToSave };
    }
  };

  // Função para alternar PDV na tabela
  const handleTogglePdv = async (product) => {
    try {
        const newValue = !product.pdvEnabled;
        
        // 1. Atualização Otimista
        setRows(prev => prev.map(p => 
            p.id === product.id ? { ...p, pdvEnabled: newValue } : p
        ));

        // 2. Persistência
        await updateDoc(doc(db, 'products', product.id), {
            pdvEnabled: newValue
        });

    } catch (error) {
        console.error("Erro ao alterar status PDV:", error);
        alert("Erro ao salvar status. Atualize a página.");
        // Reverte se falhar
        setRows(prev => prev.map(p => 
            p.id === product.id ? { ...p, pdvEnabled: !product.pdvEnabled } : p
        ));
    }
  };

  const handleDelete = async (row) => {
    if (!row || !row.id) {
      console.error('Tentativa de exclusão com ID inválido:', row);
      alert(
        'Erro ao excluir: este produto está com ID inválido no cadastro. Atualize o registro antes de tentar excluir novamente.'
      );
      return;
    }

    const ok = window.confirm(
      `Excluir permanentemente o produto "${row.name || row.sku}"?`
    );
    if (!ok) return;

    await deleteDoc(doc(db, 'products', row.id));
    await fetchProducts();
  };

  return (
    <>
      <div className="flex flex-col h-screen bg-gray-50 text-gray-900 overflow-hidden">
        
        {/* 1. CABEÇALHO ESTATICO (Não Rola) */}
        <div className="flex-none bg-white px-6 pt-4 pb-0 shadow-sm border-b border-gray-200 z-20">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-brand-gold">
                Gestão de Produtos
              </h1>
              <p className="text-xs text-gray-500">
                Painel híbrido: visualize produtos prontos para venda e estoque técnico pendente.
              </p>
              <p className="text-[11px] text-gray-400 mt-1">
                Recomendados (Separamos para Você): {currentRecommendedCount}/5
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => handleOpenForm(null)}
                className="inline-flex items-center gap-2 bg-brand-gold text-brand-black font-semibold px-4 py-2 rounded hover:bg-yellow-500 shadow-sm"
              >
                <Plus className="h-4 w-4" /> Novo Produto
              </button>
              <button
                onClick={onPrint}
                className="inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-50 shadow-sm transition-colors"
              >
                <Printer className="h-4 w-4" /> Imprimir
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-4 mb-2 flex-wrap">

            <div className="flex items-center gap-2 lg:border-r lg:border-gray-200 lg:pr-4">
              <span className="text-sm text-gray-900 font-semibold flex items-center gap-1">
                <ArrowUpDown className="w-3 h-3" /> Ordenar:
              </span>
              <select
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
                className="bg-white border border-brand-gold/50 rounded px-2 py-2 text-sm font-medium text-gray-900 focus:border-brand-gold focus:ring-1 focus:ring-brand-gold outline-none cursor-pointer hover:bg-yellow-50/50"
              >
                <option value="name_asc">A - Z (Nome)</option>
                <option value="name_desc">Z - A (Nome)</option>
                <option value="price_asc">💲 Menor Preço</option>
                <option value="price_desc">💲 Maior Preço</option>
                <option value="newest">🆕 Mais Recentes</option>
              </select>
            </div>

            <div className="flex flex-wrap gap-3 items-center">

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 flex items-center gap-1">
                  <ImageIcon className="w-3 h-3" /> Imagens:
                </span>
                <select
                  value={imageFilter}
                  onChange={(e) => setImageFilter(e.target.value)}
                  className="bg-white border border-gray-300 rounded px-2 py-2 text-sm text-gray-900 focus:border-brand-gold outline-none"
                >
                  <option value="Todos">Todas</option>
                  <option value="Com">Com Imagem</option>
                  <option value="Sem">Sem Imagem</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Estoque:</span>
                <select
                  value={stockFilter}
                  onChange={(e) => setStockFilter(e.target.value)}
                  className="bg-white border border-gray-300 rounded px-2 py-2 text-sm text-gray-900 focus:border-brand-gold outline-none"
                >
                  <option>Todos</option>
                  <option value="Com">Com</option>
                  <option value="Sem">Sem</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Local:</span>
                <select
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  className="bg-white border border-gray-300 rounded px-2 py-2 text-sm text-gray-900 focus:border-brand-gold outline-none w-28"
                >
                  {LOCATION_OPTIONS.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Status:</span>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="bg-white border border-gray-300 rounded px-2 py-2 text-sm text-gray-900 focus:border-brand-gold outline-none w-36"
                >
                  {STATUS_OPTIONS.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">PDV:</span>
                <select
                  value={pdvFilter}
                  onChange={(e) => setPdvFilter(e.target.value)}
                  className="bg-white border border-gray-300 rounded px-2 py-2 text-sm text-gray-900 focus:border-brand-gold outline-none"
                >
                  <option value="Todos">Todos</option>
                  <option value="PDV">Sim</option>
                  <option value="FORA_PDV">Não</option>
                </select>
              </div>

              <label className="flex items-center gap-2 cursor-pointer bg-yellow-50 px-3 py-1.5 rounded border border-yellow-200 hover:bg-yellow-100 transition-colors">
                <input
                  type="checkbox"
                  checked={recommendedFilter}
                  onChange={(e) => setRecommendedFilter(e.target.checked)}
                  className="accent-brand-gold w-4 h-4"
                />
                <span className="text-sm text-yellow-800 font-medium flex items-center gap-1">
                  <Star className="w-3 h-3 fill-yellow-600 text-yellow-600" /> Recomendado
                </span>
              </label>

            </div>

            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2 top-2.5 text-gray-400 h-4 w-4" />
              <input
                type="text"
                value={qSearch}
                onChange={(e) => setQSearch(e.target.value)}
                placeholder="Buscar por nome, SKU ou cód. técnico..."
                className="w-full pl-8 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:border-brand-gold focus:ring-1 focus:ring-brand-gold outline-none transition-all"
              />
            </div>
          </div>
        
          {/* Barra de Resumo (Linha Única + Fixa no Topo) */}
          <div className="bg-blue-50/50 rounded-t-lg py-2 px-4 border-t border-x border-gray-200 flex flex-row items-center justify-between gap-6 whitespace-nowrap overflow-x-auto shadow-sm text-sm">
              <div className="flex items-center gap-2">
                  <span className="text-gray-500 font-bold uppercase text-xs">Total:</span>
                  <span className="font-bold text-gray-800">{summary.count} itens ({summary.totalStock} un)</span>
              </div>

              <div className="h-4 w-px bg-gray-300"></div>

              <div className="flex items-center gap-2">
                  <span className="text-gray-500 font-bold uppercase text-xs">Venda:</span>
                  <span className="font-bold text-green-700">{fmtBRL(summary.saleValue)}</span>
              </div>

              <div className="h-4 w-px bg-gray-300"></div>

              <div className="flex items-center gap-2">
                  <span className="text-gray-500 font-bold uppercase text-xs">Custo:</span>
                  <span className="font-bold text-orange-700">{fmtBRL(summary.costValue)}</span>
              </div>

              <div className="h-4 w-px bg-gray-300"></div>

              <div className="flex items-center gap-2">
                  <span className="text-gray-500 font-bold uppercase text-xs">Margem:</span>
                  <span className="font-bold text-purple-700">{fmtBRL(summary.saleValue - summary.costValue)}</span>
              </div>
          </div>
        </div>

        {/* 2. ÁREA DE DADOS (Scroll Independente) */}
        <div className="flex-1 overflow-hidden relative bg-white">
            <div className="absolute inset-0 overflow-auto pb-10">
                <table className="w-full text-sm text-left border-collapse">
                  <thead className="text-xs text-gray-500 uppercase bg-gray-50 border-b border-gray-200 sticky top-0 z-10 shadow-sm">
                    <tr>
                      <th className="px-6 py-3 font-semibold bg-gray-50">Produto</th>
                      <th className="px-6 py-3 font-semibold text-center bg-gray-50">SKU</th>
                      {/* [GS-DIAGNOSTIC]: Coluna Custo Temporária (Mantida para conferência) */}
                      <th className="px-6 py-3 font-semibold text-center bg-yellow-50 text-yellow-800 border-x border-yellow-100">Custo</th>
                      <th className="px-6 py-3 font-semibold text-center bg-gray-50">Preço</th>
                      <th className="px-6 py-3 font-semibold text-center bg-gray-50">Estoque</th>
                      <th className="px-6 py-3 font-semibold text-center bg-gray-50">Status</th>
                      <th className="px-6 py-3 font-semibold text-center bg-gray-50">PDV</th>
                      <th className="px-6 py-3 font-semibold text-center bg-gray-50">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {loading ? (
                      <tr>
                        <td colSpan="8" className="p-8 text-center text-gray-500">
                          Carregando catálogo...
                        </td>
                      </tr>
                    ) : err ? (
                      <tr>
                        <td colSpan="8" className="p-8 text-center text-red-500">
                          {err}
                        </td>
                      </tr>
                    ) : processedRows.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="p-8 text-center text-gray-500">
                          Nenhum produto encontrado com estes filtros.
                        </td>
                      </tr>
                    ) : (
                      processedRows.map((row) => {
                        const hasImg =
                          Array.isArray(row.images) && row.images.length > 0;
                        const thumb = hasImg
                          ? row.images[0]
                          : 'https://placehold.co/48x48?text=GS';
                        const finalPrice = getEffectiveSalePrice(row);
                        const costPrice = Number(row.cost || 0);

                        return (
                          <tr key={row.id} className="hover:bg-gray-50 group transition-colors">
                            <td className="px-6 py-3">
                              <div className="flex items-center gap-3">
                                <div className="w-12 h-12 bg-gray-100 rounded-md overflow-hidden flex-shrink-0 border border-gray-200">
                                  <img
                                    src={thumb}
                                    alt="thumb"
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                                <div>
                                  <div className="font-medium text-gray-900 line-clamp-1" title={getDisplayName(row)}>
                                    {getDisplayName(row)}
                                  </div>
                                  <div className="text-xs text-gray-500 line-clamp-1">
                                    {row.supplierProductName || 'Sem nome técnico'}
                                  </div>
                                  {row.displaySettings?.onSeparamos && (
                                    <span className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                                      <Star className="w-3 h-3 fill-yellow-600 text-yellow-600" />
                                      Recomendado
                                    </span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-3 text-center font-mono text-xs text-gray-600">
                              {row.sku || '—'}
                            </td>
                            {/* [GS-DIAGNOSTIC]: Célula de Custo */}
                            <td className="px-6 py-3 text-center font-mono text-xs bg-yellow-50/30 text-yellow-800 border-x border-yellow-100">
                                {fmtBRL(costPrice)}
                            </td>
                            <td className="px-6 py-3 text-center font-medium text-gray-900">
                              {fmtBRL(finalPrice)}
                            </td>
                            <td className="px-6 py-3 text-center">
                              <span
                                className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                                  (row.stock || 0) > 0
                                    ? 'bg-green-100 text-green-800 border border-green-200'
                                    : 'bg-red-50 text-red-600 border border-red-100'
                                }`}
                              >
                                {row.stock || 0} un
                              </span>
                            </td>
                            <td className="px-6 py-3 text-center">
                              <div className="flex flex-col items-center gap-1">
                                <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200">
                                  {row.status || 'Disponível'}
                                </span>
                                <span className="text-[10px] text-gray-400">
                                  {row.location || 'Em Loja'}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-3 text-center">
                                <input 
                                    type="checkbox"
                                    checked={!!row.pdvEnabled}
                                    onChange={() => handleTogglePdv(row)}
                                    className="w-4 h-4 text-brand-gold rounded focus:ring-brand-gold cursor-pointer"
                                    title="Mostrar no PDV"
                                />
                            </td>
                            <td className="px-6 py-3 text-center">
                              <div className="flex items-center justify-center gap-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                                <button
                                  onClick={() => handleOpenForm(row)}
                                  className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                  title="Editar"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDelete(row)}
                                  className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                  title="Excluir"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
            </div>
        </div>

        {/* 3. RODAPÉ (Fixo Embaixo) */}
        <div className="flex-none bg-gray-50 px-6 py-2 border-t border-gray-200 text-xs text-gray-500 flex justify-between items-center z-20">
            <span>
              Mostrando {processedRows.length} registro(s)
            </span>
            <span>
               Ecossistema GoldenSisters v2.0
            </span>
        </div>

      </div>

      {/* ========================================================================
        LAYOUT DE IMPRESSÃO (RELATÓRIO)
        Visível apenas quando window.print() é chamado
        ========================================================================
      */}
      <div id="printable-list" className="hidden print:block bg-white text-black p-4">
        <div className="mb-6 border-b-2 border-black pb-4">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold uppercase tracking-wide">Golden Sisters</h1>
              <h2 className="text-sm text-gray-600 mt-1">Relatório de Estoque e Produtos</h2>
            </div>
            <div className="text-right text-[10px] text-gray-500">
              <p>Total Itens: {processedRows.length}</p>
              <p>Gerado em: {new Date().toLocaleString('pt-BR')}</p>
            </div>
          </div>
        </div>

        <table className="w-full text-[10px] text-left border-collapse">
          <thead>
            <tr className="border-b-2 border-black">
              <th className="py-1 pr-2 w-16 font-bold uppercase">SKU</th>
              <th className="py-1 pr-2 font-bold uppercase">Produto</th>
              <th className="py-1 pr-2 font-bold uppercase text-center w-12">Qtd</th>
              <th className="py-1 pr-2 font-bold uppercase text-right w-20">Preço</th>
              <th className="py-1 font-bold uppercase w-24 text-right">Status/Local</th>
            </tr>
          </thead>
          <tbody>
            {processedRows.map((row, idx) => (
              <tr key={row.id} className={`border-b border-gray-200 align-top ${idx % 2 === 0 ? 'bg-gray-50 print:bg-transparent' : ''}`}>
                <td className="py-1 pr-2 font-mono">
                  {row.sku || '-'}
                </td>
                <td className="py-1 pr-2">
                  <span className="font-bold block">{getDisplayName(row)}</span>
                  <span className="text-[9px] text-gray-500 block">{row.supplierProductName}</span>
                </td>
                <td className="py-1 pr-2 text-center font-bold">
                  {row.stock || 0}
                </td>
                <td className="py-1 pr-2 text-right">
                  {fmtBRL(getEffectiveSalePrice(row))}
                </td>
                <td className="py-1 text-right">
                  <div className="flex flex-col">
                    <span>{row.status || 'Disponível'}</span>
                    <span className="text-[9px] text-gray-500">{row.location}</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}